import { useEffect, useId, useRef, useCallback, type ReactNode, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/Button'
import { ModalContext, useModalTitleId } from '@/components/ui/modalContext'
import { useEscapeKey } from '@/hooks/ui/useEscapeKey'
import { useFocusTrap } from '@/hooks/ui/useFocusTrap'
import { useSwipeDismiss } from '@/hooks/ui/useSwipeDismiss'
import { CloseIcon } from '@/icons'

// Button-compatible variants/sizes. Mirrors the Button component's public
// props; kept local to avoid reaching into Button.tsx internals (Group B owns
// that file). If Button exports these types later, swap in the real imports.
type ModalButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'orange' | 'purple'
// ModalPrimaryButton always renders a text label (children), so icon-only
// sizing is excluded here — Button's size="icon" requires an aria-label at
// the type level which primary buttons satisfy via their visible text.
type ModalButtonSize = 'sm' | 'md'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  className?: string
}

function ModalRoot({ isOpen, onClose, children, className }: ModalProps) {
  const titleId = useId()
  const focusTrapRef = useFocusTrap(isOpen)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const { swipeRef, swipeHandlers, swipeStyle, backdropOpacity } = useSwipeDismiss({
    isActive: isOpen,
    onDismiss: onClose,
  })

  const { ref: swipeableRef, ...swipeProps } = swipeHandlers
  const composedRef = useCallback(
    (el: HTMLDivElement | null) => {
      ;(focusTrapRef as React.MutableRefObject<HTMLDivElement | null>).current = el
      ;(swipeRef as React.MutableRefObject<HTMLDivElement | null>).current = el
      swipeableRef(el)
    },
    [focusTrapRef, swipeRef, swipeableRef],
  )

  // Capture the element that opened the modal so we can return focus on close.
  // This layers on top of useFocusTrap's own restore so we have an explicit
  // ref + effect cleanup that won't regress if useFocusTrap's behavior changes.
  useEffect(() => {
    if (isOpen) {
      returnFocusRef.current = document.activeElement as HTMLElement | null
      return () => {
        const target = returnFocusRef.current
        // Defer so the closing element unmounts before we restore focus.
        if (target && typeof target.focus === 'function') {
          queueMicrotask(() => {
            try {
              target.focus()
            } catch {
              /* element may have detached while closing; safe to ignore. */
            }
          })
        }
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [isOpen])

  useEscapeKey(onClose, isOpen)

  if (!isOpen) return null

  return createPortal(
    <ModalContext.Provider value={{ titleId }}>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/50 animate-fade-in"
          style={{ opacity: backdropOpacity }}
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          ref={composedRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={`relative ${className ?? 'card max-w-md w-full'}`}
          style={swipeStyle}
          {...swipeProps}
        >
          {children}
        </div>
      </div>
    </ModalContext.Provider>,
    document.body,
  )
}

// Sub-components
function ModalHeader({ children, onClose }: { children: ReactNode; onClose?: () => void }) {
  const titleId = useModalTitleId()

  return (
    <div className="flex items-center justify-between mb-4">
      <h2 id={titleId} className="text-xl font-bold text-primary">
        {children}
      </h2>
      {onClose && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="p-1"
          aria-label="Close"
          icon={<CloseIcon className="w-4 h-4" />}
        />
      )}
    </div>
  )
}

function ModalBody({ children }: { children: ReactNode }) {
  return <div className="space-y-4">{children}</div>
}

function ModalActions({ children }: { children: ReactNode }) {
  return <div className="flex gap-2 mt-6">{children}</div>
}

interface ModalPrimaryButtonProps {
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  variant?: ModalButtonVariant
  size?: ModalButtonSize
  children: ReactNode
}

function ModalPrimaryButton({
  onClick,
  disabled,
  loading,
  variant = 'primary',
  size = 'md',
  children,
}: ModalPrimaryButtonProps) {
  return (
    <Button onClick={onClick} disabled={disabled} loading={loading} variant={variant} size={size} className="flex-1">
      {children}
    </Button>
  )
}

function ModalSecondaryButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <Button variant="secondary" onClick={onClick} disabled={disabled}>
      {children}
    </Button>
  )
}

// Form variant
interface ModalFormProps {
  onSubmit: (e: FormEvent) => void
  children: ReactNode
}

function ModalForm({ onSubmit, children }: ModalFormProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {children}
    </form>
  )
}

interface ModalFormActionsProps {
  submitLabel?: string
  cancelLabel?: string
  isSubmitting?: boolean
  isSubmitDisabled?: boolean
  onCancel: () => void
}

function ModalFormActions({
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isSubmitting,
  isSubmitDisabled,
  onCancel,
}: ModalFormActionsProps) {
  return (
    <div className="flex gap-2 mt-6">
      <Button type="submit" disabled={isSubmitting || isSubmitDisabled} loading={isSubmitting} className="flex-1">
        {submitLabel}
      </Button>
      <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
        {cancelLabel}
      </Button>
    </div>
  )
}

// Export as compound component
export const Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  Body: ModalBody,
  Actions: ModalActions,
  PrimaryButton: ModalPrimaryButton,
  SecondaryButton: ModalSecondaryButton,
  Form: ModalForm,
  FormActions: ModalFormActions,
})
